import React, { useState } from 'react';
import Button3D from '../ui/Button3D';
import TenantBrand from '../shared/TenantBrand';
import '../../styles/companyChoice.css';

// BL-408 G2: shown only to a person with two or more companies whose sign-in session has not chosen one yet.
// Lists ONLY the companies the server returned for this person (mcs_my_companies). It never names the door's company
// unless it is one of theirs, and it never says whether any other company exists.
// White-label: the door's brand leads at the top; "Powered by smarTEKnowledgy" sits in the footer.

const FAILURE_TEXT = {
    not_available: 'That company could not be opened. Choose another one, or ask your administrator.',
    failed: 'That company could not be opened right now. Check your connection and try again.',
};

export function CompanyList({ companies, busyId, onChoose, label = 'Your companies' }) {
    return (
        <ul className="ia-mcs-list" aria-label={label}>
            {companies.map((company) => (
                <li key={company.orgId}>
                    <Button3D
                        variant={company.isActive ? 'go' : 'primary'}
                        className="ia-mcs-company"
                        disabled={Boolean(busyId) || company.canEnter === false}
                        aria-current={company.isActive ? 'true' : undefined}
                        onClick={() => onChoose(company)}
                    >
                        <span className="ia-mcs-company__name">{company.name}</span>
                        {busyId === company.orgId && <span className="ia-mcs-company__state">Opening…</span>}
                        {company.canEnter === false && <span className="ia-mcs-company__state">Not available</span>}
                    </Button3D>
                </li>
            ))}
        </ul>
    );
}

export default function ChooseCompanyScreen({ companies, notice = null, platformName = null, footer, onChoose, onSignOut, theme = 'light' }) {
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState('');

    async function choose(company) {
        setBusyId(company.orgId);
        setError('');
        let result;
        try {
            result = await onChoose(company);
        } catch (failure) {
            result = { ok: false, reason: 'failed' };
        }
        // On success the page reloads; on a refusal the person stays here and can pick again.
        if (!result?.ok) {
            setBusyId(null);
            if (result?.reason !== 'mfa_required' && result?.reason !== 'sign_in_again') {
                setError(FAILURE_TEXT[result?.reason] || FAILURE_TEXT.failed);
            }
        }
    }

    return (
        <div className="tenant-sign-in ia-mcs flex min-h-screen items-center justify-center p-6" data-theme={theme}>
            <div className="w-full max-w-md">
                <TenantBrand platformName={platformName} />
                <section className="ia-mcs-card" aria-labelledby="ia-mcs-heading">
                    <h1 id="ia-mcs-heading" className="ia-mcs-heading">Choose a company</h1>
                    <p className="ia-mcs-intro">Your sign-in opens more than one company. Pick the one to work in now. You can switch later.</p>
                    {notice === 'mfa_required' && (
                        <p className="ia-mcs-notice" role="status">That company asks for a code from your authenticator first.</p>
                    )}
                    <CompanyList companies={companies} busyId={busyId} onChoose={choose} />
                    {error && <p role="alert" className="ia-mcs-error">{error}</p>}
                    <Button3D variant="light" className="ia-mcs-signout" disabled={Boolean(busyId)} onClick={onSignOut}>Sign out</Button3D>
                </section>
            </div>
            {footer && <footer className="tenant-sign-in-footer">{footer}</footer>}
        </div>
    );
}
